/**
 * Folders dropped from the desktop. A drop of "Day 1" with "Chair 2" inside
 * arrives as directory entries; this walks them and remembers, per file, the
 * folder it came from ("Day 1/Chair 2"), so the uploader can file each one
 * into a raw folder of the same name. A folder picked through an
 * <input webkitdirectory> carries the same information in webkitRelativePath.
 */
const FOLDER_OF = new WeakMap<File, string>();

export function tagFolder(f: File, folder: string): void {
  if (folder) FOLDER_OF.set(f, folder);
}

/** The folder a file was dropped or picked from, relative to what was dropped; null for a loose file. */
export function droppedFolder(f: File): string | null {
  const tagged = FOLDER_OF.get(f);
  if (tagged) return tagged;
  const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || '';
  if (rel.includes('/')) return rel.slice(0, rel.lastIndexOf('/'));
  return null;
}

type Entry = { isFile: boolean; isDirectory: boolean; name: string; fullPath: string };
type FileEntry = Entry & { file: (ok: (f: File) => void, err?: (e: unknown) => void) => void };
type DirEntry = Entry & { createReader: () => { readEntries: (ok: (es: Entry[]) => void, err?: (e: unknown) => void) => void } };

function readAll(dir: DirEntry): Promise<Entry[]> {
  // readEntries hands back batches of up to 100 and an empty batch at the end
  return new Promise((resolve, reject) => {
    const reader = dir.createReader();
    const out: Entry[] = [];
    const step = () => reader.readEntries((es) => { if (!es.length) resolve(out); else { out.push(...es); step(); } }, reject);
    step();
  });
}

async function walk(entry: Entry, folder: string, out: File[]): Promise<void> {
  if (entry.isFile) {
    const f = await new Promise<File>((ok, err) => (entry as FileEntry).file(ok, err));
    tagFolder(f, folder);
    out.push(f);
    return;
  }
  if (entry.isDirectory) {
    const here = folder ? `${folder}/${entry.name}` : entry.name;
    for (const child of await readAll(entry as DirEntry)) await walk(child, here, out);
  }
}

/** Every file in a drop, folders walked, each tagged with its folder. Falls back to the flat list. */
export async function filesFromDataTransfer(dt: DataTransfer): Promise<File[]> {
  const items = Array.from(dt.items ?? []);
  const entries = items.map((it) => (typeof (it as DataTransferItem & { webkitGetAsEntry?: () => Entry | null }).webkitGetAsEntry === 'function'
    ? (it as DataTransferItem & { webkitGetAsEntry: () => Entry | null }).webkitGetAsEntry() : null));
  if (!entries.some(Boolean)) return Array.from(dt.files ?? []);
  const out: File[] = [];
  try {
    for (const e of entries) if (e) await walk(e, '', out);
  } catch {
    return Array.from(dt.files ?? []);
  }
  return out.length ? out : Array.from(dt.files ?? []);
}

/** How many distinct folders a list of files came from. */
export function droppedFolders(files: File[]): string[] {
  const seen = new Map<string, string>();
  for (const f of files) { const d = droppedFolder(f); if (d && !seen.has(d.toLowerCase())) seen.set(d.toLowerCase(), d); }
  return Array.from(seen.values());
}
